/**
 * InHomeSupportCard: the client's in-home support on Life & logistics
 * (0235): a set of kinds, each with its own provider.
 *
 * The card lists one line per kind in the card's order, "{Kind}:
 * {provider}", a Directory provider's phone beside it (read from the
 * Directory on every load, as Care providers do), and Other with its note.
 * A Directory provider's name is a link to its Directory page, with the
 * href and look of the Care providers card's Directory link (LP-42); a
 * typed name stays plain text. With nothing ticked it reads "Not
 * recorded".
 *
 * Edit opens the dialog: the nine tick boxes (the house CheckboxRow). No
 * in-home support may sit beside Lives alone only: ticking it clears every
 * kind of help, and ticking a kind of help clears it. A ticked kind of help
 * shows its own provider box, the
 * Directory name box over people and organizations that the Care providers
 * and contact forms use: a pick is the Directory entry, a name typed with
 * no pick is kept as typed. Other shows its required note. Unticking a kind
 * clears its provider and note. Save sends the whole set to PUT
 * clients/{id}/in-home-support; a failed save keeps the dialog and what was
 * typed. Edit is drawn for edit_client_details (the server's gate).
 */

import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { Button } from '@/components/primitives/Button';
import { CheckboxRow } from '@/components/primitives/CheckboxRow';
import { Dialog } from '@/components/primitives/Dialog';
import { Field, fieldBoxAria } from '@/components/primitives/Field';
import { TextInput } from '@/components/primitives/TextInput';
import { FLD } from '@/components/primitives/modalFormStyles';
import { DirectoryNameBox } from '@/components/clientRecord/DirectoryNameBox';
import { CARD_ACCENTS, usePersistedCardOpen } from '@/components/clientRecord/cardSpine';
import { useTenant } from '@/lib/auth';
import { canEditClientDetails, canWriteClinicalRecord } from '@/lib/capabilities';
import { useSetClientInHomeSupport } from '@/lib/clientsApi';
import type { InHomeSupportKindWire, InHomeSupportWire } from '@/lib/clientRecord';
import {
  IN_HOME_SUPPORT_KINDS,
  IN_HOME_SUPPORT_KIND_WORDS,
  IN_HOME_SUPPORT_NOT_HELP,
  type InHomeSupportKind,
  type InHomeSupportKindInput,
} from '@/lib/inHomeSupportKinds';
import { formatPhoneDisplay } from '@/lib/phone';
import { pageWriteText, serverSentence } from '@/lib/errorCopy';
import { fieldSentence } from '@/lib/fieldErrorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';

const NOTE_LABEL = 'What other support';

// The Care providers card's Directory link (ProviderLinkAffordance in
// cardVariants.tsx), at the line's own size: the name is the link text.
// Inline, not inline-flex, so a long name wraps in the line's flow with
// the mark after its last word rather than dropping to a block of its own.
const DIRECTORY_LINK_CLS =
  'font-medium text-sage-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded-tight';

/** One kind's line on the card: its words, then the note (Other) and the
 *  provider's name, a Directory provider's name as a link to its page. */
function KindLine({ k, tenantSlug }: { k: InHomeSupportKindWire; tenantSlug: string }): ReactNode {
  const words = IN_HOME_SUPPORT_KIND_WORDS[k.kind];
  const p = k.provider;
  const note = k.note !== null && k.note !== '' ? k.note : null;
  const name = p !== null && p.name !== '' ? p.name : null;
  if (note === null && name === null) return words;
  let provider: ReactNode = null;
  if (p !== null && name !== null) {
    provider =
      p.kind === 'text' ? (
        name
      ) : (
        <Link
          to={`/t/${tenantSlug}/${p.kind === 'individual' ? 'individuals' : 'organizations'}/${p.id}`}
          data-testid={`in-home-support-provider-link-${k.kind}`}
          data-link-kind={p.kind}
          className={DIRECTORY_LINK_CLS}
        >
          {name}
          <ExternalLink size={12} aria-hidden="true" className="ml-1 inline align-[-1px]" />
        </Link>
      );
  }
  return (
    <>
      {words}: {note}
      {note !== null && provider !== null && ' · '}
      {provider}
    </>
  );
}

export function InHomeSupportCard({ inHomeSupport }: { inHomeSupport: InHomeSupportWire }): ReactNode {
  // The Edit is edit_client_details, narrowed to a viewer who keeps the
  // record: a SPICE-only therapist reads the card with no door.
  const whoami = useTenant().whoami;
  const canEdit = canEditClientDetails(whoami) && canWriteClinicalRecord(whoami);
  // The Directory link's href, built as the Care providers card builds it.
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const [editing, setEditing] = useState(false);
  const { open, toggle } = usePersistedCardOpen('card-in-home-support', undefined);

  return (
    <section
      data-testid="card-in-home-support"
      className={`rounded-card bg-paper shadow-sh1 border border-sage-800/[8%] p-4 border-l-4 ${CARD_ACCENTS.logistics.band}`}
    >
      <div className={`flex items-start justify-between gap-2 ${open ? 'mb-2' : ''}`}>
        <div className="flex items-center gap-2 min-w-0">
          <Button
            variant="ghost"
            size="sm"
            className="!min-h-0 -ml-1 !px-1 !py-0.5"
            data-testid="section-collapse"
            aria-expanded={open}
            aria-controls={open ? 'in-home-support-body' : undefined}
            aria-label={open ? 'Collapse In-home support' : 'Expand In-home support'}
            icon={open ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
            onClick={toggle}
          />
          <h3 className={`font-display text-xl ${CARD_ACCENTS.logistics.heading}`}>In-home support</h3>
        </div>
        {canEdit && (
          <Button variant="ghost" size="sm" data-testid="in-home-support-edit" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )}
      </div>
      {open && (
        <div id="in-home-support-body" className="text-sm text-sage-800">
          {inHomeSupport.kinds.length === 0 ? (
            <div data-testid="in-home-support-line">Not recorded</div>
          ) : (
            <ul className="flex flex-col gap-1">
              {inHomeSupport.kinds.map((k) => {
                const p = k.provider;
                const phone = p !== null && p.kind !== 'text' && p.phone !== null && p.phone !== '' ? formatPhoneDisplay(p.phone) : null;
                return (
                  <li key={k.kind} data-testid={`in-home-support-line-${k.kind}`}>
                    <KindLine k={k} tenantSlug={tenantSlug} />
                    {phone !== null && <span className="text-ink-soft"> {phone}</span>}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      {editing && <InHomeSupportDialog current={inHomeSupport} onClose={() => setEditing(false)} />}
    </section>
  );
}

/** One kind's state in the dialog: the box's text and the Directory link
 *  it holds, if any. */
interface KindDraft {
  ticked: boolean;
  text: string;
  link: { id: string; type: 'organization' | 'individual' } | null;
  note: string;
}

const EMPTY_DRAFT: KindDraft = { ticked: false, text: '', link: null, note: '' };

function draftsOf(current: InHomeSupportWire): Record<InHomeSupportKind, KindDraft> {
  const drafts = Object.fromEntries(IN_HOME_SUPPORT_KINDS.map((k) => [k, { ...EMPTY_DRAFT }])) as Record<InHomeSupportKind, KindDraft>;
  for (const k of current.kinds) {
    const p = k.provider;
    drafts[k.kind] = {
      ticked: true,
      text: p?.name ?? '',
      link: p !== null && p.kind !== 'text' ? { id: p.id, type: p.kind } : null,
      note: k.note ?? '',
    };
  }
  return drafts;
}

/** The body's entry for a ticked kind: a link as its id, else the typed
 *  name as text; Lives alone and No in-home support take no provider, and
 *  only Other a note. */
function entryOf(kind: InHomeSupportKind, d: KindDraft): InHomeSupportKindInput {
  const text = d.text.trim();
  const helps = !IN_HOME_SUPPORT_NOT_HELP.includes(kind);
  const provider = helps ? d.link : null;
  return {
    kind,
    provider_org_id: provider?.type === 'organization' ? provider.id : null,
    provider_individual_id: provider?.type === 'individual' ? provider.id : null,
    provider_text: !helps || provider !== null || text === '' ? null : text,
    note: kind === 'other' ? d.note.trim() || null : null,
  };
}

/** The nine tick boxes, a provider box per ticked kind of help, Other's note. */
export function InHomeSupportDialog({ current, onClose }: { current: InHomeSupportWire; onClose: () => void }): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const save = useSetClientInHomeSupport(tenantSlug);
  const contact = useSupportContact();
  const [drafts, setDrafts] = useState<Record<InHomeSupportKind, KindDraft>>(() => draftsOf(current));
  const [noteGap, setNoteGap] = useState<string | null>(null);

  const set = (kind: InHomeSupportKind, patch: Partial<KindDraft>): void =>
    setDrafts((cur) => ({ ...cur, [kind]: { ...cur[kind], ...patch } }));

  async function pressSave(): Promise<void> {
    if (save.isPending) return;
    if (drafts.other.ticked && drafts.other.note.trim() === '') {
      setNoteGap(fieldSentence(NOTE_LABEL, { kind: 'text' }, drafts.other.note));
      return;
    }
    const kinds = IN_HOME_SUPPORT_KINDS.filter((k) => drafts[k].ticked).map((k) => entryOf(k, drafts[k]));
    try {
      await save.mutateAsync({ id: clientId, kinds });
      onClose();
    } catch {
      // The sentence renders from save.error below; the dialog keeps what was typed.
    }
  }

  return (
    <Dialog
      title="In-home support"
      size="md"
      testId="in-home-support-dialog"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={save.isPending}
            loading={save.isPending}
            data-testid="in-home-support-save"
            onClick={() => void pressSave()}
          >
            Save
          </Button>
        </>
      }
    >
      <fieldset className="flex min-w-0 flex-col gap-1 py-2">
        <legend className="sr-only">Caregiving at home</legend>
        {IN_HOME_SUPPORT_KINDS.map((kind) => {
          const d = drafts[kind];
          const boxId = `in-home-support-provider-${kind}`;
          return (
            <div key={kind} className="flex min-w-0 flex-col gap-1">
              <CheckboxRow
                id={`in-home-support-kind-${kind}`}
                testId={`in-home-support-kind-${kind}`}
                label={IN_HOME_SUPPORT_KIND_WORDS[kind]}
                checked={d.ticked}
                disabled={save.isPending}
                onChange={(ticked) => {
                  // Unticking clears the kind's provider and note. No in-home
                  // support and the kinds of help clear each other.
                  setDrafts((cur) => {
                    const next = { ...cur, [kind]: ticked ? { ...cur[kind], ticked } : { ...EMPTY_DRAFT } };
                    if (ticked && kind === 'no_support') {
                      for (const k of IN_HOME_SUPPORT_KINDS) if (!IN_HOME_SUPPORT_NOT_HELP.includes(k)) next[k] = { ...EMPTY_DRAFT };
                    } else if (ticked && !IN_HOME_SUPPORT_NOT_HELP.includes(kind)) {
                      next.no_support = { ...EMPTY_DRAFT };
                    }
                    return next;
                  });
                  if ((kind === 'other' && !ticked) || (kind === 'no_support' && ticked)) setNoteGap(null);
                }}
              />
              {d.ticked && kind === 'other' && (
                <div className="pl-[30px]">
                  <Field label={NOTE_LABEL} required htmlFor="in-home-support-note" error={noteGap ?? undefined}>
                    <TextInput
                      id="in-home-support-note"
                      data-testid="in-home-support-note"
                      className={FLD}
                      value={d.note}
                      maxLength={500}
                      onChange={(e) => {
                        setNoteGap(null);
                        set(kind, { note: e.target.value });
                      }}
                      {...fieldBoxAria('in-home-support-note', { required: true, error: noteGap !== null })}
                    />
                  </Field>
                </div>
              )}
              {d.ticked && !IN_HOME_SUPPORT_NOT_HELP.includes(kind) && (
                <div className="pl-[30px]">
                  <Field label="Provider" htmlFor={boxId}>
                    <DirectoryNameBox
                      id={boxId}
                      testId={boxId}
                      value={d.text}
                      // A keystroke drops the link: an edited pick is a typed name.
                      onChange={(text) => set(kind, { text, link: null })}
                      onPick={(entry) => set(kind, { text: entry.name, link: { id: entry.id, type: entry.type } })}
                      listLabel="Directory results"
                      linked={d.link !== null}
                      boxAria={fieldBoxAria(boxId, {})}
                    />
                  </Field>
                </div>
              )}
            </div>
          );
        })}
      </fieldset>
      {save.error && (
        <p role="alert" data-testid="in-home-support-error" className="text-xs text-pepper">
          {/* A seat that reads the record without keeping it reads the
              server's own sentence (care_record_read_only). */}
          {serverSentence(save.error, 'record_edits') ?? pageWriteText(save.error, contact)}
        </p>
      )}
    </Dialog>
  );
}
